import {
  h,
  resolveComponent,
  defineAsyncComponent,
  markRaw
} from "vue";
import {
  createRouter,
  createWebHistory
 } from "vue-router";
import jwt_decode from "jwt-decode";
import store from "@/store";
import {
  getToken,
  removeToken
} from "@/services/jwtService";
import {
  getLoginBranch,
  removeLoginBranch
} from "@/services/loginBranch";
import {
  isAccessTimestampExpired,
  setIntentRoute,
  isAccessTimestampHitThreshold,
  setExtendAccessTimeStamp
} from "@/services/accessTracking";
import { openModal, closeModal } from "@/store/modal";

const routes = [
  {
    path: "/:pathMatch(.*)*",
    name: "PageNotFound",
    component: () => import("@/views/PageNotFound"),
  },
  {
    path: "/error",
    name: "Error",
    component: () => import("@/views/GeneralErrorPage"),
  },
  {
    path: "",
    component: {
      render: () => h(resolveComponent("router-view")),
    },
    beforeEnter: async (to, from, next) => {
      store.commit("showLoading", true);
      if (getLoginBranch()) {
        store.dispatch("updateSelectedBranch", getLoginBranch());
      }
      const isLogin = getToken();
      const userInfo = isLogin
        ? store.dispatch("getLoginUserInfo", null, { root: true })
        : Promise.resolve();
      try {
        // Fetch initial data
        await Promise.all([
          store.dispatch("getInit", null, { root: true }),
          userInfo,
        ]).then(() => {
          const branches = isLogin
            ? store.dispatch(
              "getCustomerBranches",
              store.getters.loginUserInfo.id,
              { root: true }
            )
            : store.dispatch("getBranches", null, { root: true });
          Promise.all([branches]);
        });
      } catch (error) {
        console.log(error);
      }
      store.commit("showLoading", false);
      return next();
    },
    children: [
      // Routes in Main Layout (MenuLayout)
      {
        path: "",
        component: () => import("@/Layout/MenuLayout.vue"),
        children: [
          {
            path: "home",
            name: "Home",
            beforeEnter: async (to, from, next) => {
              store.commit("showLoading", true);

              const actions = [];
              // services default init as null
              if (store.getters.services === null) {
                actions.push("getServices")
              }
              // popularServices default init as null
              if (store.getters.popularServices === null) {
                actions.push("getPopularServices")
              }
              // promotionServices default init as null
              if (store.getters.promotionServices === null) {
                actions.push("getPromotionServices")
              }
              // paymentMethods default init as []
              if (store.getters.paymentMethods.length <= 0) {
                actions.push("getPaymentMethods")
              }
              // branchBookingDateRange default init as []
              if (store.getters.branchBookingDateRange.length <= 0) {
                actions.push("getBranchBookingDateRange")
              }
              if (actions.length > 0) {
                try {
                  actions.forEach((action) => store.dispatch(action));
                } catch (error) {
                  console.log(error);
                }
              }

              if (getToken()) {
                const loginBranch = getLoginBranch();
                store.dispatch("updateSelectedBranch", loginBranch);
                if (store.getters.loginUserInfo === null) {
                  Promise.all([
                    store.dispatch("getLoginUserInfo", null, { root: true }),
                  ]).then(() => {
                    store.dispatch(
                      "getCustomerBranches",
                      store.getters.loginUserInfo.id
                    );
                  });
                }
              }

              store.commit("showLoading", false);
              return next();
            },
            component: () => import("../views/Home"),
          },
          {
            path: "",
            redirect: "home",
          },
          {
            path: "estore",
            name: "Estore",
            beforeEnter: async (to, from, next) => {
              // if allowEstore(Consumer Web App Setting) is not enable, redirect to error page
              if (store.getters.init.allowEstore != true) {
                router.push("/error");
              }

              store.commit("showLoading", true);

              const actions = [];
              // services default init as null
              if (store.getters.serviceSales === null) {
                actions.push("getServiceSales");
              }
              // popularServices default init as null
              if (store.getters.popularServiceSales === null) {
                actions.push("getPopularServiceSales");
              }
              // retailProducts default init as null
              if (store.getters.retailProducts === null) {
                actions.push("getRetailProducts");
              }
              // popularRetailProducts default init as null
              if (store.getters.popularRetailProducts === null) {
                actions.push("getPopularRetailProducts");
              }
              // coupons default init as null
              if (store.getters.coupons === null) {
                actions.push("getCoupons");
              }
              // popularCoupons default init as null
              if (store.getters.popularCoupons === null) {
                actions.push("getPopularCoupons");
              }
              // paymentMethods default init as []
              if (store.getters.eStorePaymentMethods.length <= 0) {
                actions.push("getEStorePaymentMethods")
              }
              // cart
              const isLogin = getToken();
              if (isLogin) {
                actions.push("getCarts");
              }

              if (actions.length > 0) {
                try {
                  await Promise.all(actions.map((action) => store.dispatch(action)));
                } catch (error) {
                  console.log(error);
                }
              }

              store.commit("showLoading", false);

              next();
            },
            component: () => import("../views/Home/Estore"),
          },
          {
            path: "account",
            name: "Account",
            meta: { requiresAuth: true },
            beforeEnter: async (to, from, next) => {
              // If data not yet fetched
              // if (store.getters.loginUserInfo === null) {
              store.commit("showLoading", true);
              try {
                await store.dispatch("getLoginUserInfo");
              } catch (error) {
                console.log(error);
              }
              store.commit("showLoading", false);
              // }

              return next();
            },
            component: () => import("@/views/MyAccount"),
          },
        ],
      },
      // Route in Details Page Layout (DetailsPageLayout)
      {
        path: "",
        component: () => import("@/Layout/DetailsPageLayout.vue"),
        children: [
          {
            path: "login",
            name: "Login",
            meta: {
              hideForAuth: true,
            },
            component: () => import("../views/Login"),
          },
          {
            path: "forgot-password",
            meta: { hideForAuth: true },
            component: {
              render: () => h(resolveComponent("router-view")),
            },
            children: [
              {
                path: "",
                name: "ForgotPassword",
                component: () => import("../views/ForgotPassword"),
              },
              {
                path: "reset",
                name: "SendResetRequest",
                component: () => import("../views/ForgotPassword/SendResetRequest"),
              },
            ],
          },
          {
            path: "/signup",
            meta: {
              hideForAuth: true,
              registerState: true
            },
            component: {
              render: () => h(resolveComponent("router-view")),
            },
            children: [
              {
                path: "",
                name: "SignUp",
                component: () => import("../views/SignUp"),
              },
              {
                path: "account-exists",
                name: "AccountExists",
                component: () => import("../views/SignUp/AccountExists"),
              },
              {
                path: "multiple-profile",
                name: "MultipleProfile",
                component: () => import("../views/SignUp/MultipleProfile"),
              },
              {
                path: "update-email",
                name: "UpdateEmail",
                component: () => import("../views/SignUp/UpdateEmail"),
              },
              {
                path: "otp",
                name: "OtpVerification",
                component: () => import("../views/SignUp/OtpVerification"),
              },
            ],
          },
          {
            path: "about",
            name: "AboutUs",
            component: () => import("../views/Home/About"),
          },
          {
            path: "branch",
            name: "SelectBranch",
            component: () => import("../views/Home/Branch"),
          },
          {
            path: "services",
            name: "Services",
            component: () => import("../views/Home/Services"),
          },
          {
            path: "serviceSales",
            name: "ServiceSales",
            beforeEnter: async (to, from, next) => {
              // if allowEstore(Consumer Web App Setting) is not enable, redirect to error page
              if (store.getters.init.allowEstore != true) {
                router.push("/error");
              }

              next();
            },
            component: () => import("../views/Home/ServiceSales"),
          },
          {
            path: "retailProducts",
            name: "RetailProducts",
            beforeEnter: async (to, from, next) => {
              // if allowEstore(Consumer Web App Setting) is not enable, redirect to error page
              if (store.getters.init.allowEstore != true) {
                router.push("/error");
              }

              next();
            },
            component: () => import("../views/Home/RetailProducts"),
          },
          {
            path: "coupons",
            name: "Coupons",
            beforeEnter: async (to, from, next) => {
              // if allowEstore(Consumer Web App Setting) is not enable, redirect to error page
              if (store.getters.init.allowEstore != true) {
                router.push("/error");
              }

              next();
            },
            component: () => import("../views/Home/Coupons"),
          },
          {
            path: "promotion",
            name: "Promotion",
            beforeEnter: async (to, from, next) => {
              if (store.getters.promotionServices === null) {
                store.commit("showLoading", true);
                try {
                  await store.dispatch("getPromotionServices");
                } catch (error) {
                  console.log(error);
                }
                store.commit("showLoading", false);
              }
              next();
            },
            component: () => import("../views/Home/Promotion"),
          },
          // Make Appointment
          {
            path: "make-appointment",
            component: {
              render: () => h(resolveComponent("router-view")),
            },
            children: [
              {
                path: "",
                name: "MakeAppointment",
                beforeEnter: async (to, from, next) => {
                  if (
                    Object.keys(store.getters.branchBookingDateRange).length ===
                    0
                  ) {
                    store.commit("showLoading", true);
                    await store.dispatch("getBranchBookingDateRange");
                    store.commit("showLoading", false);
                  }
                  next();
                },
                component: () => import("../views/Home/MakeAppointment"),
              },
              {
                path: "payment",
                component: {
                  render: () => h(resolveComponent("router-view")),
                },
                children: [
                  {
                    path: "",
                    name: "Payment",
                    beforeEnter: async (to, from, next) => {
                      const isLogin = getToken();
                      if (isLogin) {
                        await store.dispatch("getLoginUserInfo", null, {
                          root: true,
                        });
                      }
                      next();
                    },
                    component: () => import("../views/Home/MakeAppointment/Payment"),
                  },
                ],
              },
            ],
          },
          {
            path: "my-cart",
            meta: { requiresAuth: true },
            component: {
              render: () => h(resolveComponent("router-view")),
            },
            children: [
              {
                path: "",
                name: "MyCart",
                beforeEnter: async (to, from, next) => {
                  // if allowEstore(Consumer Web App Setting) is not enable, redirect to error page
                  if (store.getters.init.allowEstore != true) {
                    router.push("/error");
                  }

                  next();
                },
                component: () => import("../views/MyCart"),
              },
              {
                path: "checkout",
                component: {
                  render: () => h(resolveComponent("router-view")),
                },
                children: [
                  {
                    path: "",
                    name: "CheckOut",
                    beforeEnter: async (to, from, next) => {
                      // if allowEstore(Consumer Web App Setting) is not enable, redirect to error page
                      if (store.getters.init.allowEstore != true) {
                        router.push("/error");
                      }

                      const isLogin = getToken();
                      if (isLogin) {
                        await store.dispatch("getLoginUserInfo", null, {
                          root: true,
                        });
                      }

                      next();
                    },
                    component: () => import("../views/MyCart/CheckOut"),
                  },
                ],
              },
            ]
          },
          // My Booking
          {
            path: "booking",
            meta: { requiresAuth: true },
            component: {
              render: () => h(resolveComponent("router-view")),
            },
            children: [
              {
                path: "review",
                name: "ReviewMyBooking",
                component: () => import("../views/MyAccount/MyBooking/ReviewBooking"),
              },
            ],
          },
          // My Account
          {
            path: "account",
            meta: { requiresAuth: true },
            component: {
              render: () => h(resolveComponent("router-view")),
            },
            children: [
              {
                path: "appointment",
                name: "AppointmentHistory",
                component: {
                  render: () => h(resolveComponent("router-view")),
                },
                children: [
                  {
                    path: "",
                    name: "AppointmentHistory",
                    component: () => import("../views/MyAccount/AppointmentHistory"),
                  },
                  {
                    path: "review",
                    name: "ReviewHistoryBooking",
                    component: () => import("../views/MyAccount/AppointmentHistory/Review"),
                  },
                ],
              },
              {
                path: "booking",
                name: "MyBooking",
                meta: { requiresAuth: true },
                component: () => import("../views/MyAccount/MyBooking"),
              },
              {
                path: "package",
                name: "MyRedemption",
                beforeEnter: async (to, from, next) => {
                  if (!store.getters.init.allowCustomerRedemption) {
                    router.push("/error");
                  }

                  next();
                },
                meta: { requiresAuth: true },
                component: () => import("../views/MyAccount/MyRedemption"),
              },
              {
                path: "salon",
                name: "MySalon",
                meta: { requiresAuth: true },
                component: () => import("../views/MyAccount/MySalon"),
              },
              {
                path: "purchase",
                name: "PurchaseHistory",
                component: () => import("../views/MyAccount/PurchaseHistory"),
              },
            ],
          },
        ],
      },
      {
        path: "resetPassword/:id",
        name: "ResetPassword",
        meta: { hideForAuth: true },
        component: () => import("../views/ResetPassword"),
      },
      {
        path: "setPassword/:id",
        name: "SetPassword",
        meta: { hideForAuth: true },
        component: () => import("../views/ResetPassword"),
      },
      {
        path: "booking/cancel",
        name: "BookingCanceled",
        component: () => import("../views/MyAccount/MyBooking/CancelBooking"),
      },
      {
        path: "make-appointment/payment",
        meta: { requiresAuth: true },
        component: {
          render: () => h(resolveComponent("router-view")),
        },
        children: [
          {
            path: "success",
            name: "BookingDone",
            meta: { requiresAuth: true },
            component: () => import("../views/Home/MakeAppointment/BookingDone"),
          },
          {
            path: "failed",
            name: "BookingFailed",
            meta: { requiresAuth: true },
            component: () => import("../views/Home/MakeAppointment/BookingFailed"),
          },
          {
            path: "completed",
            name: "BookingSuccess",
            meta: { requiresAuth: true },
            component: () => import("../views/Home/MakeAppointment/BookingDone"),
          },
        ],
      },
      {
        path: "my-cart/checkout",
        meta: { requiresAuth: true },
        component: {
          render: () => h(resolveComponent("router-view")),
        },
        children: [
          // online gateway
          {
            path: "online-payment",
            name: "CartStripeOnlinePayment",
            component: () => import("@/views/MyCart/PaymentGateway/StripePayment"),
          },
          // checkout status page
          {
            path: "success",
            name: "CheckOutSuccess",
            meta: { requiresAuth: true },
            component: () => import("../views/MyCart/CheckOutSuccess.vue"),
          },
          {
            path: "failed",
            name: "CheckOutFailed",
            meta: { requiresAuth: true },
            component: () => import("../views/MyCart/CheckOutFailed.vue"),
          },
          {
            path: "completed",
            name: "CheckOutCompleted",
            meta: { requiresAuth: true },
            component: () => import("../views/MyCart/CheckOutSuccess.vue"),
          },
        ],
      },
      {
        path: "account/profile",
        name: "Profile",
        meta: { requiresAuth: true },
        component: () => import("../views/MyAccount/MyProfile"),
      },
      {
        path: "make-appointment/payment/online-payment",
        name: "OnlinePayment",
        component: () => import("../views/Home/MakeAppointment/PaymentGateway/StripePayment"),
      },
      {
        path: "account/card",
        component: {
          render: () => h(resolveComponent("router-view")),
        },
        children: [
          {
            path: "",
            name: "MyCards",
            component: () => import("../views/MyAccount/MyCard"),
          },
          {
            path: "add",
            name: "AddCard",
            component: () => import("../views/MyAccount/MyCard/AddCard"),
          },
        ],
      },
    ],
  },
];

// function prefixRoutes(prefix, meta, routes, beforeEnter) {
//   return routes.map((route) => {
//     route.path = `${prefix}${route.path}`;

//     if (Object.keys(meta).length !== 0) {
//       route.meta = { ...meta };
//     }
//     // Add BeforeEnter
//     if (beforeEnter) {
//       route["beforeEnter"] = beforeEnter;
//     }

//     return route;
//   });
// }

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    // Auto scroll to top, when route changes
    document.getElementById("app").scrollIntoView({
      behavior: "smooth",
    });
  },
});

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const hideForAuth = to.matched.some((record) => record.meta.hideForAuth);
  const registerState = to.matched.some((record) => record.meta.registerState);
  if (getLoginBranch()) {
    store.dispatch("updateSelectedBranch", getLoginBranch());
  }
  const isLogin = getToken();

  if (!registerState) {
    // Remove register temporary data
    store.commit("updateRegisterTemporary", {});
    // Remove duplicate customer temporary data
    store.commit("updateDuplicateCustomerTemporary", {});
  }

  let invalidLogin = true;
  if (!isLogin) {
    // Remove customer info when logout or token expired
    store.commit("getLoginUserInfo", null);
    store.commit("getAppointmentHistory", null);
    store.commit("getBookingList", null);
    store.commit("getSavedCards", null);
    store.commit("getPurchaseHistory", null);
    store.dispatch("resetCart");
  } else {
    const { exp } = jwt_decode(isLogin);
    const expDate = exp * 1000;
    invalidLogin = Date.now() > expDate; // here mean expired login token
    if (invalidLogin) {
      removeToken();
      removeLoginBranch();

      await new Promise(() => {
        openModal(
          {
            id: "tokenExpiredModal",
            title: 'Session expired',
          },
          markRaw(defineAsyncComponent(() => import("@/components/ModalContentComponent.vue"))),
          {
            content: "Your session has expired, Please Log in again.",
          },
          {},
          [
            {
              text: 'Ok',
              class: 'scss-blue-button',
              clickFn: () => {
                closeModal();
                router.push('/home');
              },
            }
          ]
        );
      });

      await store.dispatch("getBranches");
    }
  }

  isLogin && !invalidLogin && store.getters.branches.length <= 0 ?
    await store.dispatch("getLoginUserInfo", null, { root: true })
    : Promise.resolve();

  if (isAccessTimestampExpired()) {
    if (store.getters.branches.length <= 0) {
      if (isLogin && !invalidLogin) {
        await store.dispatch(
          "getCustomerBranches",
          store.getters.loginUserInfo?.id, // note: for some scenario, the loginUserInfo is null
        )
      } else {
        await store.dispatch("getBranches");
      }
    }

    const excluseForceBranchSelectionRoute = [
      "SelectBranch",
      "SignUp",
      "Login",
      "AccountExists",
      "MultipleProfile",
      "UpdateEmail",
      "OtpVerification",
      "ForgotPassword",
      "SendResetRequest",
      "ResetPassword",
      "SetPassword",
      "Error",
      "CartStripeOnlinePayment",
      "CheckOutSuccess",
      "CheckOutFailed",
      "CheckOutCompleted",
      "OnlinePayment",
      "BookingDone",
      "BookingFailed",
      "BookingSuccess",
      "BookingCanceled",
    ];

    if (excluseForceBranchSelectionRoute.indexOf(to.name) < 0) {
      const { path, name, params, query } = to;
      setIntentRoute(path, name, params, query);

      await new Promise(() => {
        openModal(
          {
            id: "forceBranchSelectionModal",
            cancelButton: false,
          },
          markRaw(defineAsyncComponent(() => import("@/views/Home/Branch/index.vue"))),
          {
            id: "forceBranchSelectionModal",
          },
          {
            "update:branch": () => {
              closeModal();
            },
          }
        );
      });
    }
  } else if (isAccessTimestampHitThreshold()) {
    setExtendAccessTimeStamp();
  }

  if (requiresAuth && !isLogin) {
    store.commit("showLoading", true);
    next("/login");
  } else if (hideForAuth && isLogin) {
    next("/");
  } else {
    next();
  }
});

export default router;
